import { createGlobalState } from '@vueuse/core';
import type { VxBadgeColor } from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import type { Thread, ThreadCountItem, ThreadFilterParams } from '~/services/threads/threadsClient.types';
import type { ThreadCategoryType } from '~/services/intelligentInboxSettings/intelligentInboxSettingsClient.types';
import {
    DEFAULT_STALE_WINDOW_DAYS,
    DELETED_CATEGORY_ID,
    THREAD_SECTIONS,
    type ThreadSectionValue,
} from '~/constants/messageHub';
import { aiClient, threadsClient } from '~/services';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useIntelligentInboxSettings } from '~/composables/useIntelligentInboxSettings';
import { useToasted } from '~/composables/useToasted';
import debounce from '~/utils/debounce';
import { catchRequestError } from '~/utils/request';
import {
    effectiveThreadSection,
    filtersToParams,
    isThreadDone,
    isThreadVisible,
    type ThreadTiming,
} from '~/components/message-hub/messageHubUtil';
import { defaultIntelligentInboxFilters, type IntelligentInboxThreadFilters } from './intelligentInboxFilters';

export type IntelligentSection = {
    sectionValue: ThreadSectionValue;
    label: string;
    badgeColor: VxBadgeColor;
    categories: ThreadCategoryType[];
};

export const uncategorized: ThreadCategoryType = {
    name: 'Uncategorized',
    color: '94a3b8',
    description: null,
    classification_instructions: '',
};

export const deleted: ThreadCategoryType = {
    id: DELETED_CATEGORY_ID,
    name: 'Deleted Category',
    color: '94a3b8',
    description: null,
    classification_instructions: '',
};

export const useIntelligentInbox = createGlobalState(() => {
    const toasted = useToasted();

    const teamId = Spark.state.currentTeam.id;
    const { getContext } = useCurrentGroup();
    const { getSettings } = useIntelligentInboxSettings();
    const activeSection = ref<ThreadSectionValue | null>(null);
    const categories = ref<ThreadCategoryType[] | undefined>(undefined);
    const loadingCategories = ref(false);
    const staleWindowDays = ref(DEFAULT_STALE_WINDOW_DAYS);
    const now = ref(Date.now());

    const tick = (): void => {
        now.value = Date.now();
    };

    const isDone = (thread: ThreadTiming): boolean => isThreadDone(thread, staleWindowDays.value, now.value);
    const isVisible = (thread: Pick<Thread, 'last_interaction_at'>): boolean => isThreadVisible(thread, now.value);
    const effectiveSection = (thread: ThreadTiming): string | undefined =>
        effectiveThreadSection(thread, staleWindowDays.value, now.value);

    const resolveCategory = (categoryId: number | null | undefined): ThreadCategoryType => {
        if (categoryId === null || categoryId === undefined) {
            return uncategorized;
        }
        if (categoryId === DELETED_CATEGORY_ID) {
            return deleted;
        }
        return categories.value?.find((category) => category.id === categoryId) ?? uncategorized;
    };

    const filters = ref<IntelligentInboxThreadFilters>(defaultIntelligentInboxFilters());
    const sortForward = ref(false);
    const activeCategories = ref<Set<string>>(new Set());

    const categoryKey = (sectionValue: ThreadSectionValue, categoryId: number | null | undefined): string =>
        `${sectionValue}:${categoryId ?? 'uncategorized'}`;

    const isCategoryActive = (sectionValue: ThreadSectionValue, categoryId: number | null | undefined): boolean =>
        activeCategories.value.has(categoryKey(sectionValue, categoryId));

    const setCategoryActive = (
        sectionValue: ThreadSectionValue,
        categoryId: number | null | undefined,
        value: boolean
    ): void => {
        const key = categoryKey(sectionValue, categoryId);
        const next = new Set(activeCategories.value);
        if (value) {
            next.add(key);
        } else {
            next.delete(key);
        }
        activeCategories.value = next;
    };

    const filtersCount = computed(
        () =>
            (filters.value.search ? 1 : 0) +
            (filters.value.tag ? 1 : 0) +
            (filters.value.group_id ? 1 : 0) +
            (filters.value.lifecycle_status ? 1 : 0) +
            (filters.value.membership_status ? 1 : 0)
    );

    const isDefaultView = computed(() => filtersCount.value === 0 && !sortForward.value);

    const clearFilters = (): void => {
        filters.value = defaultIntelligentInboxFilters();
        sortForward.value = false;
    };

    const inboxContext = computed(() =>
        filters.value.group_id !== '' ? { teamId, orgUnitId: undefined, groupId: filters.value.group_id } : getContext()
    );

    const countsParams = computed<ThreadFilterParams>(() =>
        filtersToParams(filters.value, teamId, { includeLifecycleMembershipFilters: true })
    );

    const fetchCategories = async (): Promise<void> => {
        if (loadingCategories.value || categories.value !== undefined) {
            return;
        }
        loadingCategories.value = true;
        try {
            const response = await getSettings();
            categories.value = response.data.categories;
            staleWindowDays.value = response.data.stale_window_days;
        } catch (err) {
            toasted.error(catchRequestError(err));
            categories.value = [];
        } finally {
            loadingCategories.value = false;
        }
    };

    const refreshCategories = async (): Promise<void> => {
        if (loadingCategories.value) {
            return;
        }
        try {
            const response = await getSettings();
            categories.value = response.data.categories;
            staleWindowDays.value = response.data.stale_window_days;
        } catch {
            // Silent fail on background refresh
        }
    };

    const deletedCategorySeen = ref(false);

    const probeDeletedCategory = async (): Promise<void> => {
        if (deletedCategorySeen.value) {
            return;
        }
        try {
            const response = await threadsClient.getThreads(inboxContext.value, {
                ...countsParams.value,
                thread_section: 'completed',
                thread_category_id: DELETED_CATEGORY_ID,
                limit: 1,
            });
            if (response.data.data.length > 0) {
                deletedCategorySeen.value = true;
            }
        } catch {
            // Silent fail; the next time Done opens probes again
        }
    };

    const sections = computed<IntelligentSection[]>(() => {
        return Object.values(THREAD_SECTIONS).map((def) => ({
            sectionValue: def.value,
            label: def.label,
            badgeColor: def.badgeColor,
            categories:
                def.value === 'completed' && deletedCategorySeen.value
                    ? [...(categories.value ?? []), uncategorized, deleted]
                    : [...(categories.value ?? []), uncategorized],
        }));
    });

    const counts = ref<ThreadCountItem[] | undefined>(undefined);
    const loadingCounts = ref(false);

    const hasLoadedInitial = computed(() => categories.value !== undefined && counts.value !== undefined);

    let latestCountsRequestId = 0;

    const fetchCounts = async (): Promise<void> => {
        const requestId = ++latestCountsRequestId;
        loadingCounts.value = true;
        try {
            const response = await threadsClient.getCounts(inboxContext.value, countsParams.value);
            if (requestId !== latestCountsRequestId) {
                return;
            }
            counts.value = response.data.data;
        } catch (error) {
            if (requestId !== latestCountsRequestId) {
                return;
            }
            toasted.error(catchRequestError(error));
            counts.value = [];
        } finally {
            if (requestId === latestCountsRequestId) {
                loadingCounts.value = false;
            }
        }
    };

    const refreshCounts = debounce(async (): Promise<void> => {
        const requestId = ++latestCountsRequestId;
        try {
            const response = await threadsClient.getCounts(inboxContext.value, countsParams.value);
            if (requestId !== latestCountsRequestId) {
                return;
            }
            counts.value = response.data.data;
        } catch {
            // Silent fail on background refresh -- next event will retry
        }
    }, 2000);

    watch(filters, () => fetchCounts(), { deep: true });

    const sectionCounts = computed<Record<string, number>>(() => {
        const result: Record<string, number> = {};
        for (const item of counts.value ?? []) {
            result[item.thread_section] = (result[item.thread_section] ?? 0) + item.count;
        }
        return result;
    });

    const categoryCounts = computed<Record<string, Record<string, number>>>(() => {
        const result: Record<string, Record<string, number>> = {};
        for (const item of counts.value ?? []) {
            const section = item.thread_section;
            if (!result[section]) {
                result[section] = {};
            }
            const categoryKey = String(item.thread_category_id ?? 'uncategorized');
            result[section][categoryKey] = item.count;
        }
        return result;
    });

    const suggestionsBucket = new Map<string, string>();
    const suggestionRequests = new Map<string, Promise<string>>();

    const suggestionKey = (groupId: string, contactId: number): string => {
        return `${groupId}:${contactId}`;
    };

    const fetchSuggestion = (groupId: string, contactId: number): Promise<string> => {
        const key = suggestionKey(groupId, contactId);

        if (suggestionsBucket.has(key)) {
            return Promise.resolve(suggestionsBucket.get(key) ?? '');
        }

        const existingRequest = suggestionRequests.get(key);
        if (existingRequest) {
            return existingRequest;
        }

        const request = aiClient
            .messageHubSuggestions({ teamId, groupId }, { contact_id: contactId })
            .then((response) => {
                const result = response.data.data;
                const reply = result?.message ?? '';
                suggestionsBucket.set(key, reply);
                return reply;
            })
            .catch((error) => {
                console.error(error);
                return '';
            })
            .finally(() => {
                suggestionRequests.delete(key);
            });

        suggestionRequests.set(key, request);

        return request;
    };

    const invalidateSuggestion = (groupId: string, contactId: number): void => {
        const key = suggestionKey(groupId, contactId);
        suggestionsBucket.delete(key);
        suggestionRequests.delete(key);
    };

    const peekSuggestion = (groupId: string, contactId: number): string =>
        suggestionsBucket.get(suggestionKey(groupId, contactId)) ?? '';

    return {
        activeSection,
        categories,
        loadingCategories,
        fetchCategories,
        refreshCategories,
        counts,
        loadingCounts,
        fetchCounts,
        refreshCounts,
        sectionCounts,
        categoryCounts,
        sections,
        probeDeletedCategory,
        fetchSuggestion,
        invalidateSuggestion,
        peekSuggestion,
        filters,
        filtersCount,
        isDefaultView,
        clearFilters,
        inboxContext,
        sortForward,
        isCategoryActive,
        setCategoryActive,
        hasLoadedInitial,
        now,
        tick,
        isDone,
        isVisible,
        effectiveSection,
        resolveCategory,
    };
});
